<template>
  <div
    class="grid-stack-item"
    :gs-id="itemData.id"
    :gs-w="itemData.w"
    :gs-h="itemData.h"
    :gs-x="itemData.x"
    :gs-y="itemData.y"
    :gs-min-w="itemData.minW"
    :gs-min-h="itemData.minH"
    :gs-max-h="8"
    :gs-pos="itemData.pos"
  >
    <!-- Remove slot icon (only visible when Alt key is pressed) -->
    <feather-icon
      icon="XIcon"
      class="slot-remove-icon"
      @click="removeSlot(itemData)"
      v-b-tooltip="'Remove'"
      v-if="isAltPressed"
    />
    <div
      ref="slotContentEl"
      class="grid-stack-item-content"
      :class="contentClasses"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop($event, itemData)"
      @click="onClickSelectSlot(itemData)"
    >
      <!-- Hidden backdrop for overlays -->
      <div style="display: none" class="backdrop" id="backdrop"></div>

      <!-- Dynamic component rendering based on slot type -->
      <component
        v-if="slotItem.type"
        :is="slotComponent"
        v-bind="componentProps"
        v-on="syncListeners"
        ref="slotContainer"
        class="slot-component"
      />

      <!-- Default empty slot message -->
      <div v-else class="default-message noselect">
        <h4 class="text-center">
          {{ $t("grid.slots.main.messages.empty_text") }}
        </h4>
      </div>
    </div>
    <!-- Slot action buttons overlay (shown when Alt is pressed and slot has content) -->
    <div class="slot-options" :class="{ show: shouldShowSlotOptions }" draggable="false">
      <!-- Remove item from slot button -->
      <feather-icon
        class="icon"
        icon="XIcon"
        size="48"
        v-b-tooltip="'Remove'"
        @click="removeItemFromSlot(itemData.id)"
      />
      <!-- Frame action button (edit/save for frame slots) -->
      <feather-icon
        class="icon"
        :icon="frameActionIcon"
        size="40"
        v-if="shouldShowFrameActions"
        @click="handleFrameAction"
        v-b-tooltip="frameActionTooltip"
      />
      <!-- Delete URL button (for frame slots with saved content) -->
      <feather-icon
        class="icon"
        icon="TrashIcon"
        size="40"
        v-if="shouldShowDeleteAction"
        v-b-tooltip="'Delete'"
        @click="deleteUrl(slotItem.data.id)"
      />
      <!-- Full screen toggle button -->
      <feather-icon
        class="icon"
        size="40"
        :icon="fullScreenIcon"
        v-b-tooltip="'Full Screen'"
        @click="goFullScreen"
      />
      <!-- Refresh slot button -->
      <feather-icon
        class="icon"
        size="40"
        icon="RefreshCwIcon"
        v-b-tooltip="'Refresh'"
        v-if="shouldShowRefreshAction"
        @click="refreshSlot(slotItem)"
      />
    </div>
  </div>
</template>

<script>
import {
  reactive,
  onMounted,
  onBeforeUnmount,
  ref,
  nextTick,
  computed,
  watch,
  getCurrentInstance,
} from "@vue/composition-api";
import store from "@/store";
import { SLOT_TYPES, SOURCES } from "@/config/layoutConfig";
import { VBTooltip } from "bootstrap-vue";
import { EventBus } from "@/libs/event-bus";
import { useToast } from "vue-toastification/composition";

// Camera slot types constant
const CAMERA_TYPES = [SLOT_TYPES.CAM_LIVE, SLOT_TYPES.CAM_PA, SLOT_TYPES.CAM_RECORDED];

// Component mapping for dynamic component loading based on slot type
const componentMap = {
  [SLOT_TYPES.CAM_LIVE]: () => import("./slots/CamSlot.vue"),
  [SLOT_TYPES.CAM_PA]: () => import("./slots/CamSlot.vue"),
  [SLOT_TYPES.CAM_RECORDED]: () => import("./slots/CamSlot.vue"),
  [SLOT_TYPES.MAP]: () => import("./slots/AlarmMapSlot.vue"),
  [SLOT_TYPES.GPS]: () => import("./slots/GpsMapSlot.vue"),
  [SLOT_TYPES.UNITS_MAP]: () => import("./slots/UnitsMapSlot"),
  [SLOT_TYPES.CUSTOM_MAP]: () => import("./slots/CustomMapSlot.vue"),
  [SLOT_TYPES.FRAME]: () => import("./slots/PageSlot.vue"),
  [SLOT_TYPES.INFO]: () => import("./slots/InfoSlot"),
  [SLOT_TYPES.INTERPHONE]: () => import("./slots/InterphoneSlot.vue"),
  [SLOT_TYPES.ACTION_PLAN]: () => import("./slots/ActionPlanSlot.vue"),
  [SLOT_TYPES.CHARTS_SDK]: () => import("./slots/ChartsSdkSlot.vue"),
  [SLOT_TYPES.CHARTS_SDK_DATA]: () => import("./slots/ChartsSdkDataSlot.vue"),
  [SLOT_TYPES.PLANE]: () => import("./slots/PlaneSlot"),
  [SLOT_TYPES.AR_TABLE_DATA]: () => import("./slots/ARTableData.vue"),
  [SLOT_TYPES.WEATHER]: () => import("./slots/WeatherSlot.vue"),
  [SLOT_TYPES.FACE_DETECTION]: () => import("./slots/FaceSearchSlot/Index.vue"),
  [SLOT_TYPES.WAZE]: () => import("./slots/WazeSlot.vue"),
  [SLOT_TYPES.CHAT]: () => import("./slots/ChatSlot"),
  [SLOT_TYPES.DETECTION_EVENT]: () => import("./slots/DetectionEventSlot.vue"),
  [SLOT_TYPES.WEB_UI]: () => import("./slots/WebUiSlot.vue"),
  [SLOT_TYPES.PRODUCT_COUNT_IMAGE]: () => import("./slots/ProductCountImage/Index.vue"),
  [SLOT_TYPES.PRODUCT_COUNT_CHART]: () => import("./slots/ProductCountChart.vue"),
  [SLOT_TYPES.PRODUCT_COUNT_CHART_HISTORY]: () =>
    import("./slots/ProductCountHistory.vue"),
  [SLOT_TYPES.FACESTREAM]: () => import("./slots/FacestreamSlot.vue"),
  [SLOT_TYPES.FACESTREAMNTECHLAB]: () => import("./slots/FacestreamNtechlabSlot.vue"),
  [SLOT_TYPES.HELPDESK]: () => import("./slots/HelpdeskSlot/TicketSlot.vue"),
  [SLOT_TYPES.DOCUMENT_ACTION_PLAN]: () => import("./slots/ActionPlanDocumentSlot.vue"),
  [SLOT_TYPES.ALARM_BRIDGE_PANEL]: () => import("./slots/AlarmBridgePanelSlot.vue"),
  [SLOT_TYPES.FR_TRACKING_MAP]: () => import("./slots/FrTrackingMapSlot.vue"),
  [SLOT_TYPES.LPR_TRACKING_MAP]: () => import("./slots/LPRTrackingMapSlot.vue"),
  [SLOT_TYPES.RISK_EVALUATION]: () => import("./slots/RiskEvaluationSlot.vue"),
  [SLOT_TYPES.OPERATIONAL_SECURITY_LIST]: () => import("./slots/OperationalSecurityListSlot.vue"),
  [SLOT_TYPES.OPERATIONAL_SECURITY_FORM]: () => import("./slots/OperationalSecurityFormSlot.vue"),
  [SLOT_TYPES.SIGNIFICANT_EVENT_LIST]: () => import("./slots/SignificantEventListSlot.vue"),
  [SLOT_TYPES.SIMULACRUM]: () => import("./slots/SimulacrumSlot.vue"),
  [SLOT_TYPES.OPERATIONAL_PROCEDURE]: () => import("./slots/OperationalProcedureSlot.vue"),
  [SLOT_TYPES.SECURITY_SELF_AUDIT]: () => import("./slots/SecuritySelfAuditSlot.vue"),
  [SLOT_TYPES.DRAGGABLE_MENU]: () => import("./slots/DraggableMenuSlot.vue"),
  [SLOT_TYPES.EMERGENCY_CONTACTS]: () => import("./slots/EmergencyContactsSlot.vue"),
  [SLOT_TYPES.MULTI_CAMERA]: () => import("./slots/MultiCameraSlot.vue"),
  [SLOT_TYPES.CHARTS_EMBEDDER]: () => import("@/components/ChartsEmbedder/Index.vue"),
  [SLOT_TYPES.NEO_DASH]: () => import("./slots/Neo4jReportsSlot.vue"),
  [SLOT_TYPES.MASTER_DATA]: () => import("@/views/operational_security/crisis_form/MasterData.vue"),
  [SLOT_TYPES.DAMAGES]: () => import("@/views/operational_security/crisis_form/Damages.vue"),
  [SLOT_TYPES.CRISIS_FACILITIES]: () => import("@/views/components/GridView/layouts/slots/CrisisSlot.vue"),
  [SLOT_TYPES.MASTER_DATA_TABLE]: () => import("@/views/operational_security/crisis_form/components/MasterDataTable.vue"),
  [SLOT_TYPES.ALLOCATIONS_BY_REGION_TABLE]: () => import("@/views/operational_security/crisis_form/components/AllocationsByRegionTable.vue"),
  [SLOT_TYPES.ALLOCATIONS_BY_MUNICIPALITY_TABLE]: () => import("@/views/operational_security/crisis_form/components/AllocationsByMunicipality.vue"),
  [SLOT_TYPES.ALLOCATIONS_BY_FACILITIES_TABLE]: () => import("@/views/operational_security/crisis_form/components/AllocationsByFacilities.vue"),
  [SLOT_TYPES.EMPLOYEE_INFORMATION_BY_FACILITY_TABLE]: () => import("@/views/operational_security/crisis_form/components/EmployeeInformationByFacility.vue"),
};

export default {
  name: "MainSlot",
  components: {},
  directives: {
    "b-tooltip": VBTooltip,
  },
  props: {
    index: {
      type: Number,
    },
    itemData: {
      type: Object,
      required: true,
    },
    tabId: {
      type: [String, Number],
    },
    slots: {
      type: Array,
    },
    pos: {
      type: Number,
    },
    isAltPressed: {
      type: Boolean,
    },
    isCard: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    // Reactive variables
    const slotContainer = ref(null);
    const slotContentEl = ref(null);
    const slotHeight = ref(0);
    const isDraggingOver = ref(false);
    const fullScreenEnabled = ref(false);
    let slotResizeObserver = null;
    const { toast } = useToast();
    const instance = getCurrentInstance();

    // Computed properties

    // Get slots for current tab
    const tabSlots = computed(() =>
      props.tabId ? store.state.grid.tabs[props.tabId].slots : []
    );

    // Get slot item data by position or fallback to item data
    const slotItem = computed(() => {
      let slot = tabSlots.value.find((s) => s.pos === props.pos);
      return slot || props.itemData;
    });

    const slotData = computed(() => store.state.grid.slotData);

    // Dynamically resolve component based on slot type
    const slotComponent = computed(() => componentMap[slotItem.value.type] || null);

    const activeTab = computed(() => store.state.grid.activeTab);
    const currentTab = computed(() => store.state.grid.tabs[props.tabId]);
    const layout = computed(() => currentTab.value.layout);

    // Check if slot should be ignored during round command execution
    const ignoredSlot = computed(
      () =>
        store.getters["round_command/isRoundCommandRunning"] &&
        store.getters["round_command/getTabId"] === props.tabId &&
        props.itemData.pos == store.getters["round_command/getIgnoredSlot"] &&
        store.getters["round_command/getIgnoredSlot"] !== 0
    );

    // Dynamic CSS classes for slot content
    const contentClasses = computed(() => ({
      "is-dragging-over": isDraggingOver.value,
      "bg-black": CAMERA_TYPES.includes(slotItem.value.type),
      slotCard: props.isCard,
    }));

    // UI visibility computed properties
    const shouldShowSlotOptions = computed(
      () => props.isAltPressed && slotItem.value.data
    );

    const shouldShowFrameActions = computed(
      () =>
        (slotItem.value.type === SLOT_TYPES.FRAME ||
          slotItem.value.type === SLOT_TYPES.NEO_DASH) &&
        slotItem.value.data.load
    );

    const shouldShowDeleteAction = computed(
      () => slotItem.value.type === SLOT_TYPES.FRAME && slotItem.value.data.id
    );

    const shouldShowRefreshAction = computed(
      () =>
        (slotItem.value.type === SLOT_TYPES.FRAME ||
          slotItem.value.type === SLOT_TYPES.NEO_DASH) &&
        slotItem.value.data.load
    );

    // Dynamic icon and tooltip computed properties
    const frameActionIcon = computed(() => {
      if (slotItem.value.type === SLOT_TYPES.NEO_DASH) return "Edit3Icon";
      return slotItem.value.data.id ? "Edit3Icon" : "SaveIcon";
    });

    const frameActionTooltip = computed(() => {
      if (slotItem.value.type === SLOT_TYPES.NEO_DASH) return "Edit";
      return slotItem.value.data.id ? "Edit" : "Save";
    });

    const fullScreenIcon = computed(() =>
      !fullScreenEnabled.value ? "Maximize2Icon" : "Minimize2Icon"
    );

    // Handle frame action (edit or save)
    const handleFrameAction = () => {
      if (slotItem.value.type === SLOT_TYPES.NEO_DASH) {
        slotItem.value.data.load = false;
        return;
      }
      if (slotItem.value.data.id) {
        resetSlotData();
      } else {
        saveSlotData();
      }
    };

    // Generate component props based on slot type
    const componentProps = computed(() => {
      const { type, data, id } = slotItem.value;
      switch (type) {
        // Camera slot types - live, PA, recorded
        case SLOT_TYPES.CAM_LIVE:
        case SLOT_TYPES.CAM_PA:
        case SLOT_TYPES.CAM_RECORDED:
          if (data?.camera?.id && (data?.camera?.camera_type || data?.camera?.device_type)) {
            return {
              parentId: props.tabId,
              data,
              type,
              tabInfo: tabInfo.value,
              slotId: id,
              key: `${type}-${data.camera.id}`,
              source: data?.in_app_source ?? "",
            };
          }
          return;

        // GPS tracking slot
        case SLOT_TYPES.GPS:
          return {
            unit_id: data?.unit_id,
            data: data?.data?.id,
          };

        // Units map slot
        case SLOT_TYPES.UNITS_MAP:
          return {
            unit_id: data?.alarm ? null : data?.unit_id,
            alarm: data?.alarm ?? null,
            tabInfo: tabInfo.value,
            data: data?.data,
            singleUnit: data?.single_unit ?? false,
          };

        // Custom map slot
        case SLOT_TYPES.CUSTOM_MAP:
          return {
            lat: data?.lat,
            lng: data?.lng,
          };

        // Info, action plan, and plane slots
        case SLOT_TYPES.INFO:
        case SLOT_TYPES.ACTION_PLAN:
        case SLOT_TYPES.PLANE:
          return { data: slotItem.value };

        // Interphone slot
        case SLOT_TYPES.INTERPHONE:
          return { data };

        // Frame/page slot
        case SLOT_TYPES.FRAME:
          return { data: slotItem.value, ref: `pageSlot-${slotItem.value.data.id}` };

        // Charts and tracking map slots
        case SLOT_TYPES.CHARTS_SDK:
        case SLOT_TYPES.CHARTS_SDK_DATA:
        case SLOT_TYPES.FR_TRACKING_MAP:
        case SLOT_TYPES.LPR_TRACKING_MAP:
        case SLOT_TYPES.DETECTION_EVENT:
          return {
            tabInfo: tabInfo.value,
            data,
          };

        // AR Table Data (requires sync for selectedObject prop)
        case SLOT_TYPES.AR_TABLE_DATA:
          return {
            selectedObject: data,
            isLoadedChart: true,
            key: data?.id,
          };

        // Alarm bridge panel slot
        case SLOT_TYPES.ALARM_BRIDGE_PANEL:
          return {
            data,
            key: data.device_proxy_id,
          };

        // Helpdesk ticket slot
        case SLOT_TYPES.HELPDESK:
          return {
            ticket: data,
          };

        // Product count slots
        case SLOT_TYPES.PRODUCT_COUNT_IMAGE:
        case SLOT_TYPES.PRODUCT_COUNT_CHART:
        case SLOT_TYPES.PRODUCT_COUNT_CHART_HISTORY:
          return {
            tabInfo: tabInfo.value,
          };

        // Web UI slot
        case SLOT_TYPES.WEB_UI:
          return {
            data,
            key: data?.id,
          };

        // Chat slot
        case SLOT_TYPES.CHAT:
          return {
            chat: data?.chat,
            key: data?.chat?.id,
          };

        // Map, facestream, and document slots
        case SLOT_TYPES.MAP:
        case SLOT_TYPES.FACESTREAM:
        case SLOT_TYPES.FACESTREAMNTECHLAB:
          return { data };

        case SLOT_TYPES.DOCUMENT_ACTION_PLAN:
          return { data };

        case SLOT_TYPES.RISK_EVALUATION:
          return { data };

        case SLOT_TYPES.OPERATIONAL_SECURITY_LIST:
        case SLOT_TYPES.OPERATIONAL_SECURITY_FORM:
        case SLOT_TYPES.SIGNIFICANT_EVENT_LIST:
        case SLOT_TYPES.DRAGGABLE_MENU:
          return {
            data,
            slotId: id
          };

        case SLOT_TYPES.EMERGENCY_CONTACTS:
          return { data };

        case SLOT_TYPES.OPERATIONAL_PROCEDURE:
          return { data };

        case SLOT_TYPES.MULTI_CAMERA:
          return { slotData: data };

        // Charts Embedder slot
        case SLOT_TYPES.CHARTS_EMBEDDER: {
          const base = {
            height: slotHeight.value,
            baseUrl: data?.baseUrl || window.__env?.VUE_APP_STREAMLIT || '',
            lang: data?.lang ?? 'es',
            live: data?.live ?? false,
            refreshInterval: data?.refreshInterval ?? 15,
            token: data?.token ?? '',
            dataset: data?.dataset ?? '',
            view: data?.view ?? '',
            filters: data?.filters ?? null,
            showControls: data?.showControls ?? true,
            showFilters: data?.showFilters ?? true,
            // Persisted chart state to restore on reopen / page reload
            savedConfig: data ?? null,
          };
          // Optional advanced Streamlit params — only forwarded when present
          const advancedKeys = [
            'agg', 'by', 'columns', 'freq', 'innerRadius', 'legendPosition',
            'orientation', 'stack', 'trendline', 'targetRange', 'unit',
            'colorMapping', 'limit', 'plaintext', 'mapping', 'query',
            'where', 'aspectRatio', 'frameToken', 'renderBackground',
            'engine', 'palette', 'configuration',
          ];
          advancedKeys.forEach(k => {
            if (data?.[k] !== undefined && data[k] !== '') base[k] = data[k];
          });
          return base;
        }

        // Neo4j reports slot
        case SLOT_TYPES.NEO_DASH:
          return { data: slotItem.value };

        // Slots without specific data requirements
        case SLOT_TYPES.WEATHER:
        case SLOT_TYPES.FACE_DETECTION:
        case SLOT_TYPES.WAZE:
          return {};

        case SLOT_TYPES.DAMAGES:
        case SLOT_TYPES.MASTER_DATA:
           return {};

        default:
          // console.warn("Unexpected slot type:", type);
          return { data: data ?? {} };
      }
    });

    // Generate sync listeners for two-way data binding
    const syncListeners = computed(() => {
      const { type, id } = slotItem.value;
      const listeners = {};

      // AR Table Data needs .sync modifier for selectedObject
      if (type === SLOT_TYPES.AR_TABLE_DATA) {
        listeners["update:selectedObject"] = (newValue) => {
          store.dispatch("grid/updateSlotData", {
            id,
            data: newValue,
          });
        };
      }

      // Charts Embedder persists its configuration so the chart can be
      // reopened after saving the view or reloading the page.
      if (type === SLOT_TYPES.CHARTS_EMBEDDER) {
        listeners["update:config"] = (config) => {
          store.dispatch("grid/updateSlotData", {
            tabId: props.tabId,
            id,
            data: config,
          });
          store.dispatch("grid/updateUserConfig");
        };
      }

      if (type === SLOT_TYPES.DETECTION_EVENT) {
        listeners["update:filters"] = (filters) => {
          store.dispatch("grid/updateSlotData", {
            tabId: props.tabId,
            id,
            data: { filters },
          });
          store.dispatch("grid/updateUserConfig");
        };
      }
      return listeners;
    });

    // Tab information object for child components
    const tabInfo = computed(() => {
      const theresDetectionEventWidget = !!tabSlots.value.find(
        (slot) => slot.type2 === SLOT_TYPES.DETECTION_EVENT
      );
      return {
        id: props.tabId,
        pos: props.itemData.pos,
        layout: layout.value,
        isActive: props.tabId === activeTab.value,
        type2: theresDetectionEventWidget ? SLOT_TYPES.DETECTION_EVENT : "default",
      };
    });

    // Watch for fullscreen changes and emit events
    watch(
      () => fullScreenEnabled.value,
      (newValue) => {
        if (newValue) {
          EventBus.emit("entering-full-screen", slotItem.value);
        } else {
          EventBus.emit("exiting-full-screen", slotItem.value);
        }
      }
    );

    // Event handlers

    // Handle slot selection click
    const onClickSelectSlot = (widget) => {
      onSelectSlot(widget);
    };

    // Remove item from slot (clear slot content)
    const removeItemFromSlot = (id) => {
      store.commit("grid/removeSlotById", {
        tabId: props.tabId,
        slotId: props.itemData.id,
      });

      store.dispatch("grid/updateUserConfig");
    };

    // Remove entire slot from grid
    const removeSlot = (item) => {
      emit("remove-slot", item.id);
    };

    // Handle slot selection logic
    const onSelectSlot = (widget) => {
      if (slotData.value?.sourceSlotId &&
          slotData.value.sourceSlotId === widget.id &&
          slotItem.value.type === SLOT_TYPES.OPERATIONAL_SECURITY_LIST) {
        return;
      }

      if (slotData.value?.data && canSlotBeAdded()) {
        const slot = findItem(props.slots, widget.id);
        if (slot) {
          let updatedSlot = Object.assign({}, slot);
          // Update slot with new data
          updatedSlot.type = slotData.value.type;
          updatedSlot.data = slotData.value.data;
          updatedSlot.name = slotData.value.name;
          updatedSlot.type2 = slotData.value.type;
          updatedSlot.x = slot.x;
          updatedSlot.y = slot.y;
          updatedSlot.w = slot.w;
          updatedSlot.h = slot.h;
          updatedSlot.pos = slot.pos;

          store.dispatch("grid/resetSlotSelection");

          if (updatedSlot) {
            // Replace existing slot with updated one
            store.dispatch("grid/removeSlotById", {
              tabId: props.tabId,
              slotId: widget.id,
            });
            store.dispatch("grid/addItemToLayout", updatedSlot);
          }
        }
      }
    };

    // Check if slot type can be added (prevent duplicates for certain types)
    const canSlotBeAdded = () => {
      if (slotData.value.type === SLOT_TYPES.DETECTION_EVENT) {
        const slot = tabSlots.value.find(
          (slot) => slot.type === SLOT_TYPES.DETECTION_EVENT
        );
        if (slot) {
          showMessage(
            $t("grid.slots.main.messages.widget_not_be_added"),
            $t("grid.slots.main.messages.detection_event_widget_exists"),
            "danger"
          );
          store.dispatch("grid/resetSlotSelection");
          return false;
        }
      }
      return true;
    };

    // Show error message toast
    const showMessage = (title, message) => {
      toast({
        title: title,
        text: message,
        type: "error",
        icon: "BellIcon",
        duration: 5000,
      });
    };

    // Reset slot data (for frame slots)
    const resetSlotData = () => {
      slotItem.value.data.load = false;
      if (slotItem.value.data?.id) {
        slotItem.value.data.save = true;
      }
    };

    // Save slot data (for frame slots)
    const saveSlotData = () => {
      slotItem.value.data.load = false;
      slotItem.value.data.save = true;
      slotItem.value.shared = true;
    };

    // Refresh slot content
    const refreshSlot = (slotItem) => {
      if (slotItem.type === SLOT_TYPES.FRAME && slotItem.data.load) {
        if (slotContainer.value && slotContainer.value.refreshPage) {
          slotContainer.value.refreshPage();
        }
      }
      if (slotItem.type === SLOT_TYPES.NEO_DASH && slotItem.data.load) {
        if (slotContainer.value && slotContainer.value.refreshPage) {
          slotContainer.value.refreshPage();
        }
      }
    };

    // Delete URL with confirmation dialog
    const deleteUrl = (id) => {
      if (instance && instance.proxy && instance.proxy.$swal) {
        instance.proxy
          .$swal({
            title: instance.proxy.$t("messages.delete.title"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: instance.proxy.$t("actions.delete"),
            customClass: {
              confirmButton: "btn btn-primary",
              cancelButton: "btn btn-outline-danger ml-1",
            },
            buttonsStyling: false,
          })
          .then((result) => {
            if (!result.isConfirmed) return;

            // Execute deletion and refresh data
            store.dispatch("pages/delete", id).then(() => {
              store.dispatch("pages/getAll");
              store.commit("grid/resetTenants");
              store.dispatch("grid/resetUnits");
              slotItem.value.data.id = null;
            });
          });
      }
    };

    // Utility function to find item by position or ID
    const findItem = (data, value) => {
      return (
        data.find((item) => item.pos === value) || data.find((item) => item.id === value)
      );
    };

    // Fullscreen event handler
    const onFullScreenChange = () => {
      fullScreenEnabled.value = !!slotContainer.fullscreenElement;
    };

    // Toggle fullscreen mode
    const goFullScreen = () => {
      const el = slotContainer.value?.$el || slotContainer.value;

      if (!document.fullscreenElement) {
        // Enter fullscreen
        if (el.requestFullscreen) {
          el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          el.webkitRequestFullscreen();
        } else if (el.msRequestFullscreen) {
          el.msRequestFullscreen();
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    };

    // Drag and drop event handlers
    const handleDragOver = (e) => {
      e.preventDefault();

      if (slotData.value?.sourceSlotId &&
          slotData.value.sourceSlotId === props.itemData.id) {
        e.dataTransfer.dropEffect = 'none';
        isDraggingOver.value = false;
        return;
      }

      e.dataTransfer.dropEffect = 'copy';
      isDraggingOver.value = true;
    };

    const handleDragLeave = () => {
      isDraggingOver.value = false;
    };

    // Handle drop events for drag and drop functionality
    const handleDrop = (e, itemData) => {
      e.preventDefault();
      const textData = e.dataTransfer.getData("text");
      const slotData = store.state.grid.slotData;

      // Handle subsystem tree drag and drop
      if (slotData && slotData.source === SOURCES.SUBSYSTEM_TREE_DND) {
        store.dispatch(slotData.type, {
          data: slotData.data,
          source: SOURCES.SUBSYSTEM_TREE,
        });
        isDraggingOver.value = false;
        return;
      }

      // Handle base64 encoded data
      if (textData) {
        if (/^[A-Za-z0-9+/=]+$/.test(textData)) {
          parseMapsSDKData(textData);
        }
      }

      onSelectSlot(itemData);
      isDraggingOver.value = false;
    };

    // Parse Maps SDK data from drag and drop
    const parseMapsSDKData = (textData) => {
      try {
        const parsedData = JSON.parse(atob(textData));

        if (parsedData.source === "unit-map") {
          const unit = store.state.grid.units.find((u) => u.id === parsedData.unit_id);
          const action = parsedData.action;

          if (unit) {
            // Handle camera setting action
            if (action === "set_camera") {
              const camera = unit.cameras.find((c) => c && c.id === parsedData.camera_id);
              store.dispatch("grid/setSlotData", {
                type: SLOT_TYPES.CAM_LIVE,
                name: camera.name,
                data: { camera },
              });
            }

            // Handle blueprint setting action
            if (action === "set_blueprint") {
              store.dispatch("grid/setSlotData", {
                type: SLOT_TYPES.PLANE,
                name: `Planos: ${unit.description}`,
                data: { planes_url: unit.unit_planes, unit_id: unit.id },
              });
            }
          }
        }
      } catch (error) {
        console.error("Error parsing maps data:", error);
      }
    };

    // Lifecycle hooks

    // Setup fullscreen event listeners and ResizeObserver on component mount
    onMounted(() => {
      document.addEventListener("fullscreenchange", onFullScreenChange);
      document.addEventListener("webkitfullscreenchange", onFullScreenChange);
      document.addEventListener("mozfullscreenchange", onFullScreenChange);

      // Observe slot content height for components that need pixel-level height
      if (slotContentEl.value) {
        slotResizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            slotHeight.value = Math.round(entry.contentRect.height);
          }
        });
        slotResizeObserver.observe(slotContentEl.value);
      }
    });

    // Clean up event listeners and ResizeObserver on component unmount
    onBeforeUnmount(() => {
      document.removeEventListener("fullscreenchange", onFullScreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullScreenChange);
      document.removeEventListener("mozfullscreenchange", onFullScreenChange);

      if (slotResizeObserver) {
        slotResizeObserver.disconnect();
        slotResizeObserver = null;
      }
    });

    // Return reactive properties and methods for template use
    return {
      // Variables
      SLOT_TYPES,
      isDraggingOver,
      slotContainer,
      slotContentEl,
      slotHeight,
      fullScreenEnabled,

      // Methods
      handleDragOver,
      handleDragLeave,
      handleDrop,
      parseMapsSDKData,
      goFullScreen,
      removeItemFromSlot,
      onClickSelectSlot,
      resetSlotData,
      saveSlotData,
      refreshSlot,
      deleteUrl,
      handleFrameAction,

      // Computed properties
      slotComponent,
      componentProps,
      slotItem,
      ignoredSlot,
      currentTab,
      tabInfo,
      syncListeners,
      removeSlot,
      contentClasses,
      shouldShowSlotOptions,
      shouldShowFrameActions,
      shouldShowDeleteAction,
      shouldShowRefreshAction,
      frameActionIcon,
      frameActionTooltip,
      fullScreenIcon,
    };
  },
};
</script>

<style lang="scss" scoped>
.main-slot {
  background-color: #f0f0f0;
  border: 1px solid #ccc;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Drag over state styling */
.is-dragging-over {
  border: 2px solid #bb0000 !important;
}

.slot-component {
  width: 100%;
  height: 100%;
}

/* Action buttons overlay styling */
.slot-options {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  min-height: 100%;
  display: none;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  z-index: 998;

  .icon {
    cursor: pointer;
    color: #fff;

    &:hover {
      color: #f0f0f0;
    }
  }

  &.show {
    display: flex;
  }
}

/* Remove slot icon styling */
.slot-remove-icon {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  cursor: pointer;
  color: #4b4b4b;
  font-size: 1.5rem;
  z-index: 999;

  &:hover {
    color: #3f3f3f;
  }
}

/* Empty slot message styling */
.default-message {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 1rem;
  opacity: 0.6;
  transition: opacity 0.3s ease;
  &:hover {
    opacity: 1;
  }

  h4 {
    color: #a5a5a5;
  }

  .dark-layout & {
    h4 {
      color: var(--third-light);
    }
  }

}

/* Dark theme styling */
.dark-layout {
  .slot-component {
    background-color: #0e1e2d;
  }

  .slot-options {
    background-color: rgba(0, 0, 0, 0.5);
  }

  .slot-remove-icon {
    color: #ffffff;
  }
}
</style>
